import React, { type MouseEventHandler } from 'react';
import CheckIcon from '@assets/img/icons/check-2.svg?react';
import { motion } from 'framer-motion';

import { timeStringFormat } from '@/common/lib/timer';
import { type ContractType } from '@/common/types/contract';

import { ContractRibbonAnimation } from '../Contract/ContractRibbonAnimation/ContractRibbonAnimation';
import { StringXSmall } from '../Typography/Typography';
import { SecondaryTimer } from '../ui/Timer/SecondaryTimer';

import css from './styles.module.scss';

import goldenFailPng from '/img/icons/golden_contract/Fail.png';
import goldenFailWebp from '/img/icons/golden_contract/Fail.webp';

interface props {
  title: string;
  type: ContractType;
  point: number;
  takeContract: MouseEventHandler<HTMLDivElement>;
  isUninitialized: boolean;
  seconds: number;
  img: { png: string; webp: string };
  isOpened?: string;
  nextGoldContractSeconds: number;
  hasContractCompleted: boolean;
  inModal?: boolean;
  buttonText?: string;
}

const GoldenAndCustomItem: React.FC<props> = ({
  title,
  type,
  point,
  takeContract,
  isUninitialized,
  isOpened,
  seconds,
  img,
  nextGoldContractSeconds,
  hasContractCompleted,
  inModal,
  buttonText,
}) => {
  const goldenFail: boolean = !!(type === 'Golden' && seconds <= 0 && isOpened);
  const complete: boolean = !!(type === 'Golden'
    ? seconds > 0 && hasContractCompleted
    : hasContractCompleted);

  return (
    <div className={css.goldenContract} onClick={takeContract}>
      {!!point && (
        <motion.div
          initial={{ opacity: 0, scale: 1.5, x: '-50%', y: '-50%' }}
          animate={{ opacity: 1, scale: 1, x: '-50%', y: '-50%' }}
          transition={{ delay: isUninitialized ? 0.6 : 0.8, duration: 0.5 }}
          className={`${css.level} ${css.goldStarContract}`}
        >
          <StringXSmall weight="bold" className="!text-black mt-0.5">
            {point}
          </StringXSmall>
        </motion.div>
      )}
      <div style={{ opacity: goldenFail ? '0.8' : '1' }}>
        <picture>
          <source srcSet={img.webp} type="image/webp" />
          <img src={img.png} alt="character" />
        </picture>
      </div>
      <div
        className={css.goldenContractInfo}
        style={{ justifyContent: isOpened ? 'space-between' : '' }}
      >
        <h3>{title}</h3>
        {!isOpened ? (
          <button type="button">
            {buttonText ? buttonText : 'Open'}
            <svg id="icon-Vector" viewBox="0 0 19 32" width={6}>
              <path
                fill="#ffd600"
                style={{
                  fill: 'var(--color1, #ffd600)',
                  stroke: 'var(--color1, #ffd600)',
                }}
                d="M3.083 3.96c0.555-0.54 1.456-0.54 2.011 0l11.378 11.062c0.555 0.54 0.555 1.415 0 1.955l-11.378 11.062c-0.555 0.54-1.456 0.54-2.011 0s-0.555-1.415 0-1.955l10.372-10.084-10.372-10.084c-0.555-0.54-0.555-1.415 0-1.955z"
              ></path>
            </svg>
          </button>
        ) : type === 'Golden' ? (
          <SecondaryTimer
            className="reward_timer"
            value={timeStringFormat(
              (seconds > 0 ? seconds : nextGoldContractSeconds) * 1000,
            )}
            variant="goldenContract"
          />
        ) : (
          <></>
        )}
      </div>
      {goldenFail && (
        <div className={css.fail}>
          <motion.div
            initial={{ opacity: 0, scale: 1.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <picture>
              <source srcSet={goldenFailWebp} type="image/webp" />
              <img src={goldenFailPng} alt="character" />
            </picture>
          </motion.div>
        </div>
      )}
      {complete && (
        <div className={css.complete}>
          <div className={css.check}>
            <CheckIcon className="w-[20px] h-[15px]" />
          </div>
        </div>
      )}
      {!inModal && (
        <ContractRibbonAnimation isUninitialized={isUninitialized} />
      )}
    </div>
  );
};

export default GoldenAndCustomItem;
